// views/notice/NoticeListView.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getNoticesApi, deleteNoticeApi, Notice, NoticeSearchParams } from "../../lib/api/notice";
import "./NoticeListView.css";

const NoticeListView = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("공지사항");
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // 초기 날짜 설정 (오늘부터 6개월 전)
  const getInitialDates = () => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(sixMonthsAgo),
      endDate: formatDate(today)
    };
  };

  const { startDate: initialStartDate, endDate: initialEndDate } = getInitialDates();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  
  // 데이터 로딩 함수
  const loadNotices = async (searchParams?: NoticeSearchParams) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: 15,
        ...searchParams,
      };
      
      try {
        // 실제 API 호출 시도
        const response = await getNoticesApi(params);
        setNotices(response.content);
        setTotalPages(response.totalPages);
      } catch (apiError) {
        // 백엔드가 실행되지 않은 경우 Mock 데이터 사용
        console.warn('백엔드 서버에 연결할 수 없습니다. Mock 데이터를 사용합니다.');
        
        const mockNotices: Notice[] = [
          {
            id: 1,
            title: "2025년 4월 민원발생시 처리 안내",
            author: "김지민",
            content: "민원 처리 관련 안내사항입니다.",
            createdAt: "2025-03-05T20:10:00",
            isFixed: false,
            viewCount: 15
          },
          {
            id: 2,
            title: "2025년 나주시 전체 사업장 컨퍼런스 일정",
            author: "김준호", 
            content: "컨퍼런스 일정 안내입니다.",
            createdAt: "2025-03-05T20:10:00",
            isFixed: false,
            viewCount: 23
          },
          {
            id: 3,
            title: "2025년 5월 행사일정",
            author: "윤봉근",
            content: "5월 행사 일정 공지입니다.",
            createdAt: "2025-03-05T20:10:00", 
            isFixed: true,
            viewCount: 45
          }
        ];
        
        setNotices(mockNotices);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('공지사항 목록 로딩 실패:', error);
      alert('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    loadNotices();
  }, [currentPage]);

  const handleTabChange = (tab: string) => {
    if (tab === "관리자정보 등록") {
      navigate("/admin");
      return;
    }
    setActiveTab(tab);
  };

  const handleRegister = () => {
    navigate("/notice/register");
  };

  const handleView = (id: number) => {
    // 상세보기는 수정 페이지로 이동 (읽기 전용이 아닌 수정 가능한 형태)
    navigate(`/notice/edit/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/notice/edit/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('이 공지사항을 정말 삭제하시겠습니까?\n삭제된 내용은 복구할 수 없습니다.')) {
      try {
        await deleteNoticeApi(id);
        alert('공지사항이 삭제되었습니다.');
        // 목록 새로고침
        loadNotices();
      } catch (error) {
        console.error('삭제 실패:', error);
        alert('공지사항 삭제에 실패했습니다.');
      }
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearch = () => {
    const searchParams: NoticeSearchParams = {};
    
    if (startDate) {
      searchParams.startDate = startDate;
    }
    if (endDate) {
      searchParams.endDate = endDate;
    }
    
    setCurrentPage(0); // 검색 시 첫 페이지로 이동
    loadNotices(searchParams);
  };

  // 날짜 포맷 함수
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="notice-list-container">
      {/* 탭 버튼 */}
      <div className="tab-buttons">
        <div className="" style={{ float: "left" }}>
          <button
            className={`tab-button ${activeTab === "공지사항" ? "active" : ""}`}
            onClick={() => handleTabChange("공지사항")}
          >
            공지사항
          </button>
          <button
            className={`tab-button ${activeTab === "관리자정보 등록" ? "active" : ""}`}
            onClick={() => handleTabChange("관리자정보 등록")}
          >
            관리자정보 등록
          </button>
          <button
            className={`tab-button ${activeTab === "로고 등록" ? "active" : ""}`}
            onClick={() => handleTabChange("로고 등록")}
          >
            로고 등록
          </button>
        </div>
        <button 
          className="btn-excel-download" 
          style={{ float: "right", background: "none", border: "none", cursor: "pointer" }}
          onClick={() => alert("엑셀 다운로드 기능은 추후 구현 예정입니다.")}
        >
          <img src={"/img/btn_excel01.png"} alt="엑셀다운로드" />
        </button>
      </div>

      {/* 컨텐츠 박스 */}
      <div className="notice-content-box">
        <div className="notice-content">
          {/* 날짜 검색 영역 */}
          <div className="search-section">
            <div className="search-left">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
              <span className="date-separator">~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
              <button className="search-btn" onClick={handleSearch}>조회</button>
            </div>
            <div className="search-right">
              <button className="btn-register" onClick={handleRegister}>
                신규등록
              </button>
            </div>
          </div>

          {/* 테이블 영역 */}
          <div className="table-section">
            <table className="notice-table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>번호</th>
                  <th style={{ width: "150px" }}>작성일</th>
                  <th>제목</th>
                  <th style={{ width: "100px" }}>작성자</th>
                  <th style={{ width: "120px" }}>공지상단고정</th>
                  <th style={{ width: "150px" }}>작업</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '50px' }}>
                      데이터를 불러오는 중...
                    </td>
                  </tr>
                ) : notices.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '50px' }}>
                      등록된 공지사항이 없습니다.
                    </td>
                  </tr>
                ) : (
                  notices.map((notice) => (
                    <tr key={notice.id} className={notice.isFixed ? "fixed-notice" : ""}>
                      <td className="row-number">{notice.id}</td>
                      <td>{formatDateTime(notice.createdAt)}</td>
                      <td className="title-cell">
                        <span className="title-link" onClick={() => handleView(notice.id)}>
                          {notice.title}
                        </span>
                      </td>
                      <td>{notice.author}</td>
                      <td className="fixed-cell">
                        {notice.isFixed && <button className="btn-fixed">고정</button>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(notice.id)}
                        >
                          수정
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(notice.id)}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div className="pagination">
            <button
              className={`page-btn ${currentPage === 0 ? "disabled" : ""}`}
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0}
            >
              «
            </button>
            <button
              className={`page-btn ${currentPage === 0 ? "disabled" : ""}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              ‹
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
              >
                {page + 1}
              </button>
            ))}

            <button
              className={`page-btn ${currentPage >= totalPages - 1 ? "disabled" : ""}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              ›
            </button>
            <button
              className={`page-btn ${currentPage >= totalPages - 1 ? "disabled" : ""}`}
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeListView;
